import React from "react";

import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@client/shadcn/ui/form";
import { ServerProjectsForms, interfaces, ProjectsTemplates_Project_T, Context } from "@reactivated";
import { ControllerRenderProps, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@client/shadcn/ui/input";
import { Slider } from "@client/shadcn/ui/slider";
import { Label } from "@client/shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@client/shadcn/ui/select";
import { toast } from "sonner";
import { Editor } from "@client/shadcn/lexical/blocks/editor-00/editor";
import { SerializedEditorState } from "lexical";
import _ from "lodash";
import { AspectRatio } from "@client/shadcn/ui/aspect-ratio";
import { Popover, PopoverContent, PopoverTrigger } from "@client/shadcn/ui/popover";
import { CalendarIcon, Check, ChevronsUpDown, TagIcon } from "lucide-react";
import { Button } from "@client/shadcn/ui/button";
import { Calendar } from "@client/shadcn/ui/calendar";
import { MultiSelect } from "@client/shadcn/ui/multi-select";
import { defaultDescription } from "@client/utils";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@client/shadcn/ui/command";


interface Props {
    className?: string;
    formData: ServerProjectsForms;
    availableTags: {name: string}[];
    availableContacts: {
        id: number;
        email: string;
        first_name: string;
        last_name: string;
    }[];
    project?: ProjectsTemplates_Project_T;
}

export const ProjectForm = (props: Props) => {
    const context = React.useContext(Context);
    const formData = props.formData;
    const formSchema = z.object({
        [formData.fields.title.name]: z.string().min(5).max(50),
        [formData.fields.splash_art.name]: z.instanceof(File).optional().refine((file) => !file || [
                "image/png",
                "image/jpeg",
                "image/jpg",
            ].includes(file.type),
            { message: "Only .jpg, .jpeg or .png file formats are accepted" },
        ).refine((file) => 
            file?.size || 0 <= 5 * 1024 * 1024,
            { message: "File size should not exceed 5MB"},
        ),
        [formData.fields.details.name]: z.custom<SerializedEditorState>().refine(
            (editorState) => editorState?.root && editorState.root.children.length > 0,
            { message: "Description is required" },
        ),
        [formData.fields.status.name]: z.union([
            z.literal("Planning"),
            z.literal("InProgress"),
            z.literal("Completed"),
            z.literal("Archived"),
        ]),
        [formData.fields.progress.name]: z.coerce.number().int().nonnegative().lte(100),
        [formData.fields.start_date.name]: z.date(),
        [formData.fields.tags.name]: z.array(z.string()).max(4),
        [formData.fields.contact.name]: z.number().int().nonnegative().optional().nullable(),
    });
    const form  = useForm<z.infer<typeof formSchema>>({
        mode: "onChange",
        resolver: zodResolver(formSchema),
        defaultValues: {
            [formData.fields.title.name]: props.project?.title,
            [formData.fields.splash_art.name]: undefined,
            [formData.fields.details.name]: props.project ? JSON.parse(props.project.details) : defaultDescription,
            [formData.fields.status.name]: props.project?.status,
            [formData.fields.progress.name]: props.project?.progress || 0,
            [formData.fields.start_date.name]: props.project ? new Date(props.project.start_date) : new Date(),
            [formData.fields.tags.name]: props.project?.tags.map((tag) => tag.name) || [],
            [formData.fields.contact.name]: props.project ? (props.project.contact?.id || undefined) : context.user.id,
        },
    });
    const fileRef = form.register("file");

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        const formData = new FormData();
        formData.append("csrfmiddlewaretoken", context.csrf_token);
        for (const [key, value] of Object.entries(values)) {
            switch (typeof value) {
                case "number": { formData.append(key, value?.toString()); break; }
                case "string": { formData.append(key, value); break; }
                case "undefined": { break; }
                default: {
                    if (value instanceof File) {
                        formData.append(key, value, value.name);
                    } else if (value instanceof Date) {
                        const offset = value.getTimezoneOffset();
                        const valueTimezoneOffset = new Date(value.getTime() - (offset * 60 * 1000));
                        formData.append(key, valueTimezoneOffset.toISOString().split('T')[0]);
                    } else if (Array.isArray(value)) {
                        formData.append(key, value.join(","));
                    } else if (key === props.formData.fields.details.name) {
                        formData.append(key, JSON.stringify(value));
                    } else if (value === null) {
                        formData.append(key, "")
                    } else {
                        throw Error("Data type not handled by form");
                    }
                    break;
                }
            }
        }
        const response = await fetch(props.project ? `/projects/${props.project.id}/` : "/projects/", {
            method: "POST",
            body: formData,
            headers: {
                Accept: "application/json",
            },
        });

        if (response.status !== 200) {
            toast.error("There was an error saving your changes", {
                description: "If the problem persists, contact the system administrator",
                position: "bottom-center",
            });
            return;
        }

        const data = (await response.json()) as interfaces.ProjectEdit | interfaces.ProjectAdd;
        for (const [fieldName, errors] of Object.entries(data.form.errors || {})) {
            form.setError(fieldName, {
                message: errors.join("\n"),
            });
        }
        if (!data.form.errors) {
            toast.success("Your changes were submitted successfully", {
                description: "Refresh the page to see your changes",
                action: {
                    label: "Refresh",
                    onClick: () => location.reload(),
                },
                position: "bottom-center",
            });
        }
    };

    return (
        <Form {...form}>
            <form
                className="py-4 px-4"
                id="projectForm"
                onSubmit={form.handleSubmit(onSubmit)}
            >
                <FormField
                    control={form.control}
                    name={formData.fields.title.name}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{formData.fields.title.label}</FormLabel>
                            <FormControl>
                                <Input {...field as ControllerRenderProps<{[x: string]: string}, string>} placeholder="Enter the project title" />
                            </FormControl>
                            <FormDescription>{formData.fields.title.help_text}</FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name={formData.fields.splash_art.name}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{formData.fields.splash_art.label}</FormLabel>
                            {props.project?.splash_art.url
                            ? <AspectRatio ratio={1 / 1} className="h-full w-full flex items-center justify-center bg-gradient-to-r from-gray-200 to-gray-300">
                                <img src={props.project.splash_art.url} alt={props.project.title.charAt(0) || ""}/>
                            </AspectRatio>
                            : null}
                            
                            <FormControl>
                                <Input
                                    type="file"
                                    {...fileRef}
                                    accept="image/png, image/jpg, image/jpeg"
                                    onChange={(event) => field.onChange(event.target.files?.item(0))}
                                />
                            </FormControl>
                            <FormDescription>{formData.fields.splash_art.help_text}</FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField 
                    control={form.control}
                    name={formData.fields.details.name}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{formData.fields.details.label}</FormLabel>
                            <FormControl>
                                <Editor
                                    {...field}
                                    editorSerializedState={form.getValues(formData.fields.details.name) as SerializedEditorState}
                                    onSerializedChange={field.onChange}
                                />
                            </FormControl>
                            <FormDescription>{formData.fields.details.help_text}</FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name={formData.fields.status.name}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{formData.fields.status.label}</FormLabel>
                            <FormControl>
                                <Select
                                    onValueChange={field.onChange}
                                    defaultValue={field.value as string}
                                    {...field}
                                    value={field.value as string}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a status"/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Planning">Planning</SelectItem>
                                        <SelectItem value="InProgress">In Progress</SelectItem>
                                        <SelectItem value="Completed">Completed</SelectItem>
                                        <SelectItem value="Archived">Archived</SelectItem>
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormDescription>{formData.fields.status.help_text}</FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name={formData.fields.progress.name}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{formData.fields.progress.label}</FormLabel>
                            <FormControl>
                                <div className="flex gap-4">
                                    <Slider
                                        {...field}
                                        value={[field.value as number]}
                                        defaultValue={[field.value as number]}
                                        min={0}
                                        max={100}
                                        step={10}
                                        onValueChange={(vals) => {
                                            field.onChange(vals[0]);
                                        }}
                                    />
                                    <Label>
                                        {field.value as number}%
                                    </Label>
                                </div>
                            </FormControl>
                            <FormDescription>{formData.fields.progress.help_text}</FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name={formData.fields.start_date.name}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{formData.fields.start_date.label}</FormLabel>
                            <Popover modal>
                                <PopoverTrigger asChild>
                                    <FormControl>
                                        <Button variant={"outline"} className={[
                                                "w-[240px] pl-3 text-left font-normal",
                                                field.value ? "" : "text-muted-foreground",
                                            ].join(" ")}>
                                            {(field.value as Date | undefined)?.toLocaleDateString() || <span>Pick a date</span>}
                                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                        </Button>
                                    </FormControl>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={field.value as Date}
                                        onSelect={field.onChange}
                                        disabled={(date) =>
                                            date < new Date("1900-01-01")
                                        }
                                        autoFocus
                                    />
                                </PopoverContent>
                            </Popover>
                            <FormDescription>{formData.fields.start_date.help_text}</FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name={formData.fields.tags.name}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{formData.fields.tags.label}</FormLabel>
                            <FormControl>
                                <MultiSelect
                                    modalPopover
                                    options={Array.from(props.availableTags.map((tag) => {
                                        return {
                                            value: tag.name,
                                            label: tag.name,
                                            icon: TagIcon,
                                        };
                                    }))}
                                    onValueChange={field.onChange}
                                    defaultValue={props.project?.tags.map((tag) => tag.name) || []}
                                    placeholder="Add tags"
                                    maxCount={4}
                                />
                            </FormControl>
                            <FormDescription>{formData.fields.tags.help_text}</FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name={formData.fields.contact.name}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{formData.fields.contact.label}</FormLabel>
                            <Popover modal>
                                <PopoverTrigger asChild>
                                    <FormControl>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            className="justify-between"
                                        >
                                            {
                                                field.value
                                                ? props.availableContacts.find((contact) => contact.id === field.value)?.email
                                                : "Select contact"
                                            }
                                            <ChevronsUpDown />
                                        </Button>
                                    </FormControl>
                                </PopoverTrigger>
                                <PopoverContent>
                                    <Command>
                                        <CommandInput placeholder="Search"/>
                                        <CommandList>
                                            <CommandEmpty>No results</CommandEmpty>
                                            <CommandGroup>
                                                {
                                                    props.availableContacts.map((contact) => (
                                                        <CommandItem
                                                            value={contact.id.toString()}
                                                            key={`${contact.first_name} ${contact.last_name}`}
                                                            keywords={[contact.first_name, contact.last_name, contact.email]}
                                                            onSelect={() => {
                                                                if (contact.id === field.value) {
                                                                    field.onChange(null);
                                                                } else {
                                                                    field.onChange(contact.id);
                                                                }
                                                            }}
                                                            className="justify-between"
                                                        >
                                                            <div className="flex flex-col gap-2">
                                                                <Label>{`${contact.first_name} ${contact.last_name}`}</Label>
                                                                <Label className="text-xs font-light text-stone-400">{contact.email}</Label>
                                                            </div>
                                                            
                                                            <Check
                                                                className={
                                                                    contact.id === field.value
                                                                    ? "opacity-100"
                                                                    : "opacity-0"
                                                                }
                                                            />
                                                        </CommandItem>
                                                    ))
                                                }
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                            <FormDescription>{formData.fields.contact.help_text}</FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </form>
        </Form>
    );
};
